import {
	createContext,
	Dispatch,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useReducer,
} from 'react';
import { useLocation } from 'react-router-dom';

import { SuccessStep } from '@mopla/constants';

import { VerificationStatus, WelcomeStep } from './constants';

export type TWelcomePageStoreAction =
	| { type: 'SHOW_PWD_MODAL'; payload: boolean }
	| { type: 'SET_AUTH_ERROR'; payload: boolean }
	| { type: 'SET_FORGOT_PWD_ERROR'; payload: boolean }
	| { type: 'SET_IS_LOADING'; payload: boolean }
	| { type: 'SET_SUCCESS_TYPE'; payload: SuccessStep | null }
	| { type: 'SET_STEP'; payload: WelcomeStep }
	| { type: 'SET_LOGIN_TITLE'; payload: ReactNode }
	| { type: 'SET_VERIFICATION_STATUS'; payload: VerificationStatus }
	| { type: 'SET_VERIFY_FAILURE'; payload: boolean }
	| { type: 'RESET_STEPS' };

interface IWelcomePageStore {
	activeStep: WelcomeStep | null;
	successType: SuccessStep | null;
	showSetPasswordModal: boolean;
	isAuthError: boolean;
	isForgotPasswordError: boolean;
	isLoading: boolean;
	loginTitleComp: ReactNode;
	verificationStatus: VerificationStatus | null;
	isVerifyFailure: boolean;
}

interface IWelcomePageContext extends IWelcomePageStore {
	setStepFromLocation: () => void;
	setStep: (step: WelcomeStep) => void;
	setSuccessType: (successType: SuccessStep | null) => void;
	setIsLoading: (isLoading: boolean) => void;
	setLoginTitle: (loginTitle: ReactNode) => void;
	dispatch: Dispatch<TWelcomePageStoreAction>;
}

const WelcomePageContext = createContext<IWelcomePageContext>(
	{} as IWelcomePageContext
);

const initialStepState: IWelcomePageStore = {
	activeStep: null,
	showSetPasswordModal: false,
	isAuthError: false,
	successType: null,
	isForgotPasswordError: false,
	isLoading: false,
	loginTitleComp: null,
	verificationStatus: null,
	isVerifyFailure: false,
};

const stepReducer = (
	state: IWelcomePageStore,
	action: TWelcomePageStoreAction
): IWelcomePageStore => {
	switch (action.type) {
		case 'SET_STEP':
			return { ...state, activeStep: action.payload };
		case 'SHOW_PWD_MODAL':
			return { ...state, showSetPasswordModal: action.payload };
		case 'SET_AUTH_ERROR':
			return { ...state, isAuthError: action.payload };
		case 'SET_FORGOT_PWD_ERROR':
			return { ...state, isForgotPasswordError: action.payload };
		case 'SET_IS_LOADING':
			return { ...state, isLoading: action.payload };
		case 'SET_SUCCESS_TYPE':
			return { ...state, successType: action.payload };
		case 'SET_LOGIN_TITLE':
			return { ...state, loginTitleComp: action.payload };
		case 'SET_VERIFICATION_STATUS':
			return { ...state, verificationStatus: action.payload };
		case 'SET_VERIFY_FAILURE':
			return { ...state, isVerifyFailure: action.payload };
		case 'RESET_STEPS':
			return initialStepState;
		default:
			return state;
	}
};

export const useWelcomePageStore = (): IWelcomePageContext => {
	const location = useLocation();

	const [state, dispatch] = useReducer(stepReducer, initialStepState);

	const setStep = useCallback((step: WelcomeStep) => {
		dispatch({ type: 'SET_STEP', payload: step });
	}, []);

	const setSuccessType = useCallback(
		(successType: SuccessStep | null) => {
			dispatch({ type: 'SET_SUCCESS_TYPE', payload: successType });
		},
		[dispatch]
	);

	const setIsLoading = useCallback(
		(isLoading: boolean) => {
			dispatch({ type: 'SET_IS_LOADING', payload: isLoading });
		},
		[dispatch]
	);

	const setLoginTitle = useCallback(
		(loginTitle: ReactNode) => {
			dispatch({ type: 'SET_LOGIN_TITLE', payload: loginTitle });
		},
		[dispatch]
	);

	const setStepFromLocation = useCallback(() => {
		if (location.search.includes(WelcomeStep.login)) {
			setStep(WelcomeStep.login);
		} else if (location.search.includes(WelcomeStep.registration)) {
			setStep(WelcomeStep.registration);
		} else if (location.search.includes(WelcomeStep.forgotPassword)) {
			setStep(WelcomeStep.forgotPassword);
		} else if (location.search.includes(WelcomeStep.resetPassword)) {
			setStep(WelcomeStep.resetPassword);
		} else if (location.search.includes(WelcomeStep.verifyEmail)) {
			setStep(WelcomeStep.verifyEmail);
		} else if (location.search.includes(WelcomeStep.verifyBeforeStart)) {
			setStep(WelcomeStep.verifyBeforeStart);
		} else if (location.search.includes(WelcomeStep.personalDetails)) {
			setStep(WelcomeStep.personalDetails);
		} else if (location.search.includes(WelcomeStep.tryOurApp)) {
			setStep(WelcomeStep.tryOurApp);
		} else if (location.search.includes(WelcomeStep.verifyAndChangeEmail)) {
			setStep(WelcomeStep.verifyAndChangeEmail);
		} else {
			dispatch({ type: 'RESET_STEPS' });
		}
	}, [location, setStep]);

	return {
		activeStep: state.activeStep,
		successType: state.successType,
		isLoading: state.isLoading,
		showSetPasswordModal: state.showSetPasswordModal,
		isAuthError: state.isAuthError,
		isForgotPasswordError: state.isForgotPasswordError,
		loginTitleComp: state.loginTitleComp,
		verificationStatus: state.verificationStatus,
		isVerifyFailure: state.isVerifyFailure,
		setStepFromLocation,
		setStep,
		setSuccessType,
		setIsLoading,
		setLoginTitle,
		dispatch,
	};
};

export const WelcomePageProvider: FC<{ children: ReactNode }> = ({
	children,
}) => {
	const store = useWelcomePageStore();

	return (
		<WelcomePageContext.Provider value={store}>
			{children}
		</WelcomePageContext.Provider>
	);
};

export const useWelcomePageStoreContext = (): IWelcomePageContext => {
	const context = useContext(WelcomePageContext);
	if (!context) {
		throw new Error(
			'useWelcomePageContext must be used within a WelcomePageProvider'
		);
	}
	return context;
};
